import { useEffect, useMemo, useRef, useState } from "react"
import { useFrame } from "@react-three/fiber"
import { dampQ } from "maath/easing"
import * as THREE from "three"

import { SOCKET_EVENTS } from "@domo/shared"
import { AscentOrb } from "./AscentOrb"
import { Avatar } from "./Avatar"
import { DescentBeam } from "./DescentBeam"
import { TargetIndicator } from "./TargetIndicator"
import type { AvatarAnimKey } from "./MaterializedMesh"
import { getSocket } from "../../lib/socket"
import {
    WALK_BOUNDARY_RADIUS,
    RING_OBSTACLE_HEIGHT,
    clampToBoundary,
    pathCrossesRing,
    projectToRingEdge,
    pathCrossesElectionPortal,
    projectToElectionPortalEdge,
} from "../../lib/domeConstants"
import { useLinkStore } from "../../stores/useLinkStore"
import { usePlayerStore } from "../../stores/usePlayerStore"

// 2026-05-06 — Tap sobre el propio avatar ya NO abre el panel de
// inspección. La lectura del Índice de Silicio + 6 pilares queda
// reservada exclusivamente a otros avatares (peers remotos): es un
// gesto de "leer al otro" en el campo, no un espejo del propio
// estado. El Tripulante consulta sus propias métricas por la HUD
// general / Mi Núcleo. Removidos: hitbox del propio avatar,
// handlers de tap selfTapState y la importación de useInspectStore
// (sólo se usaba para esto).

const SEND_INTERVAL_MS = 100 // 10 Hz baseline
// 2026-05-05 v2 — Refactor de damp3 (smoothTime exponencial) a velocidad
// CONSTANTE en unidades/segundo. damp3 daba velocidad alta inicial que
// decaía exponencialmente al acercarse al target — eso desalineaba la
// cadencia de pisadas con el avance (rápido al inicio, lento cerca del
// destino). Con velocidad constante el avatar siempre avanza al mismo
// ritmo y la animación matchea naturalmente.
//   · WALK_SPEED 1.6 u/s → cadencia normal de caminata (~1.4 m/s humano)
//   · RUN_SPEED 4.5 u/s  → trote rápido (~4.5 m/s)
// 2026-05-05 v3 — Subimos RUN_SPEED de 3.6 a 4.5 para que el avance del
// avatar matchee la cadencia de pisadas del clip de Mixamo "Run"
// reproducido a x1.3 timeScale. Con 3.6 u/s las pisadas se sentían
// más rápidas que el desplazamiento (sensación leve de skating);
// 4.5 cierra ese gap. Estos valores se ajustan junto al timeScale
// del clip en MaterializedMesh (run x1.3).
const WALK_SPEED = 1.6
const RUN_SPEED = 4.5
const ARRIVAL_EPSILON = 0.05
const POSITION_DELTA_EPSILON = 0.005

// Duración por defecto del clip de saludo. La wave de Mixamo dura
// ~3.7s; usamos 1800ms para una sensación más vivaz y para liberar el
// control de movimiento rápido.
const WAVE_DURATION_MS = 1800

// 2026-05-05 v6 — Salto.
// Duración del clip "Saltar con brazos abiertos" de Mixamo ≈ 1.0s.
// Mantenemos el avatar en estado "jumping" durante JUMP_DURATION_MS:
// durante esa ventana el avatar puede cruzar el aro obstáculo si su
// altura visual supera RING_OBSTACLE_HEIGHT.
const JUMP_DURATION_MS = 1100
// Altura máxima del arco del salto en metros. Pico al 50% del clip.
// 2026-05-05 v7 — Bajado de 1.6 a 0.95: el salto previo era demasiado
// alto (>1m de elevación visual lucía exagerado). 0.95 sigue
// permitiendo cruzar el aro obstáculo (RING_OBSTACLE_HEIGHT = 0.5):
// jumpY > 0.5 ocurre durante ~62% del arco del salto.
const JUMP_PEAK = 0.95
// Velocidad agregada en la dirección del target durante el salto. Si
// el Tripulante salta mientras camina/corre, conserva el momentum
// horizontal: además del avance normal por target, el salto le da un
// impulso forward que se siente "salto en carrera" (no levantarse en
// vertical y caer al mismo lugar).
const JUMP_FORWARD_BOOST = 1.2

// 2026-05-05 v7 — Detección de "stuck" contra el aro o el boundary.
// Si después del clamp final el avatar avanzó menos que este umbral
// pero todavía hay target activo, consideramos que está topado contra
// algo y cancelamos el target → animación pasa a idle. Si después
// salta y el cuerpo gana altura, puede retomar caminando con un nuevo
// click. El epsilon es bastante chico para no cancelar movimientos
// genuinamente lentos.
const STUCK_DELTA_EPSILON = 0.0008
// Velocidad de slerp del avatar hacia el cameraYaw cuando está idle
// en primera persona — más alto = el cuerpo gira más rápido al rotar
// la cámara. 0.18 da un seguimiento suave, no telepórtico.
const FP_IDLE_YAW_SLERP = 0.18

// Avatar local — predicción optimista del movimiento + emisión de posición.
//
// Si entra en sealed (Llave Doble completada) el avatar se bloquea — ignora
// el target y deja de emitir PLAYER_MOVE para que el server no lo mueva.
// Cuando termina el enlace, retoma su flujo normal.
//
// Sprint 3 — Si phase !== "incarnated", NO renderiza el avatar normal.
// En "descending" pintamos el haz de luz; en "materializing" el haz se
// desvanece y el avatar emerge con fade-in (materializeOpacity de 0→1).
// En "void" no hay nada (el orbe vacío vive aparte).
//
// Cualquier pointerup global suelta el reach pendiente (LINK_RELEASE).
export function LocalAvatar() {
    const groupRef = useRef<THREE.Group>(null)
    const lastSent = useRef(0)
    const lastSentPos = useRef<[number, number, number]>([0, 0, 0])
    const tempVec = useMemo(() => new THREE.Vector3(), [])

    const target = usePlayerStore((s) => s.target)
    const siliconIndex = usePlayerStore((s) => s.siliconIndex)
    const displayName = usePlayerStore((s) => s.displayName)
    const isAnchored = usePlayerStore((s) => s.isAnchored)
    const phase = usePlayerStore((s) => s.phase)
    const model = usePlayerStore((s) => s.model)
    const polarity = usePlayerStore((s) => s.polarity)
    const spawnPad = usePlayerStore((s) => s.spawnPad)
    const descentStartedAt = usePlayerStore((s) => s.descentStartedAt)
    const cameraMode = usePlayerStore((s) => s.cameraMode)
    const linkPhase = useLinkStore((s) => s.phase)

    // 2026-05-05 v7 — Quaternions auxiliares para el sync FP. Los
    // memoizamos para no allocar cada frame.
    const fpYawQuaternion = useMemo(() => new THREE.Quaternion(), [])
    const fpYawEuler = useMemo(() => new THREE.Euler(0, 0, 0, "YXZ"), [])

    // 2026-05-05 v7 — Al entrar en primera persona, alineamos cameraYaw
    // con el yaw actual del avatar. Sin esto, la cámara mira hacia el
    // último yaw que el Tripulante había rotado en una sesión previa
    // de FP (o 0 si nunca rotó), y el cuerpo se ve "girando solo" al
    // primer frame para alinearse. Inicializar cameraYaw con el yaw
    // del avatar conserva la dirección de mirada al hacer el toggle.
    useEffect(() => {
        if (cameraMode !== "firstPerson") return
        const g = groupRef.current
        if (!g) return
        fpYawEuler.setFromQuaternion(g.quaternion, "YXZ")
        usePlayerStore.getState().setCameraYaw(fpYawEuler.y)
    }, [cameraMode, fpYawEuler])

    // Sprint 3 — Posiciona el avatar en el pad cuando arranca a
    // materializar para que aparezca exactamente donde aterrizó el haz.
    // Después del fade-in puede caminar a su posición destino.
    const justMaterialized = useRef(false)
    useEffect(() => {
        if (phase === "materializing" && !justMaterialized.current) {
            justMaterialized.current = true
            if (groupRef.current) {
                groupRef.current.position.set(spawnPad[0], 0, spawnPad[2])
            }
            lastSentPos.current = [spawnPad[0], 0, spawnPad[2]]
        }
        if (phase === "void") {
            justMaterialized.current = false
        }
    }, [phase, spawnPad])

    // Posicionar el avatar en el spawn que el server asignó (una sola vez).
    useEffect(() => {
        const initial = usePlayerStore.getState().position
        if (groupRef.current) {
            groupRef.current.position.set(initial[0], initial[1], initial[2])
        }
        lastSentPos.current = [initial[0], initial[1], initial[2]]
    }, [])

    // Materialize opacity — fade-in mientras phase === "materializing".
    // Lo calculamos cada frame leyendo descentStartedAt del store.
    const [materializeOpacity, setMaterializeOpacity] = useState(
        phase === "incarnated" ? 1 : 0
    )

    // 2026-05-05 v6 — Estado de animación + acciones gestuales.
    //   · animKey  — el clip que el Avatar reproduce
    //                (idle/walk/run/wave/jump).
    //   · isSpaceDownRef — bandera viva del modificador "correr"
    //                (desktop: Space mantenido + click para correr al
    //                target en lugar de caminar).
    //   · isWavingRef — bandera viva del saludo en curso
    //                (one-shot, timer WAVE_DURATION_MS).
    //   · isJumpingRef — bandera viva del salto en curso
    //                (one-shot, timer JUMP_DURATION_MS).
    //   · jumpStartedAtRef — performance.now() del inicio del salto;
    //                LocalAvatar calcula altura del arco con esto cada
    //                frame y la suma a g.position.y.
    //
    // Mapeo de teclas (revisión Zak 2026-05-05):
    //   · Space → mantener para CORRER (en lugar de caminar) al target.
    //   · S     → SALTO (one-shot). Antes era "saludo".
    //   · H     → SALUDO (one-shot). Reemplaza a S.
    // Las teclas se escuchan al window, no al canvas, para no requerir
    // foco en el lienzo.
    const [animKey, setAnimKey] = useState<AvatarAnimKey>("idle")
    const isSpaceDownRef = useRef(false)
    const isWavingRef = useRef(false)
    const isJumpingRef = useRef(false)
    const jumpStartedAtRef = useRef<number | null>(null)
    const lastAnimDecisionRef = useRef<AvatarAnimKey>("idle")

    useEffect(() => {
        const triggerJump = () => {
            if (isJumpingRef.current) return
            isJumpingRef.current = true
            jumpStartedAtRef.current = performance.now()
            window.setTimeout(() => {
                isJumpingRef.current = false
                jumpStartedAtRef.current = null
            }, JUMP_DURATION_MS)
        }
        const triggerWave = () => {
            if (isWavingRef.current) return
            isWavingRef.current = true
            window.setTimeout(() => {
                isWavingRef.current = false
            }, WAVE_DURATION_MS)
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.repeat) return
            // Ignorar si el evento viene de un input/textarea (paneles).
            const t = e.target as HTMLElement | null
            if (
                t &&
                (t.tagName === "INPUT" ||
                    t.tagName === "TEXTAREA" ||
                    t.isContentEditable)
            ) {
                return
            }
            if (e.code === "Space") {
                isSpaceDownRef.current = true
                e.preventDefault()
            } else if (e.key === "s" || e.key === "S") {
                triggerJump()
            } else if (e.key === "h" || e.key === "H") {
                triggerWave()
            }
        }
        const onKeyUp = (e: KeyboardEvent) => {
            if (e.code === "Space") {
                isSpaceDownRef.current = false
            }
        }
        // Si el tab pierde foco, soltamos Space — evita correr eterno.
        const onBlur = () => {
            isSpaceDownRef.current = false
        }
        // 2026-05-06 — Custom events para los botones gestuales mobile.
        // En mobile no hay teclado, así que los gestos se disparan con
        // botones HTML; estos eventos sintetizan keyboardEvent's effect
        // sin contaminar el sistema de teclado.
        const onMobileJump = () => triggerJump()
        const onMobileWave = () => triggerWave()
        const onMobileRunDown = () => {
            isSpaceDownRef.current = true
        }
        const onMobileRunUp = () => {
            isSpaceDownRef.current = false
        }
        window.addEventListener("keydown", onKeyDown)
        window.addEventListener("keyup", onKeyUp)
        window.addEventListener("blur", onBlur)
        window.addEventListener("rsv-domo-jump", onMobileJump)
        window.addEventListener("rsv-domo-wave", onMobileWave)
        window.addEventListener("rsv-domo-run-down", onMobileRunDown)
        window.addEventListener("rsv-domo-run-up", onMobileRunUp)
        return () => {
            window.removeEventListener("keydown", onKeyDown)
            window.removeEventListener("keyup", onKeyUp)
            window.removeEventListener("blur", onBlur)
            window.removeEventListener("rsv-domo-jump", onMobileJump)
            window.removeEventListener("rsv-domo-wave", onMobileWave)
            window.removeEventListener("rsv-domo-run-down", onMobileRunDown)
            window.removeEventListener("rsv-domo-run-up", onMobileRunUp)
        }
    }, [])

    useFrame((_, dt) => {
        const g = groupRef.current
        if (!g) return

        // Sprint 3 — recalcular opacidad de materialización.
        if (phase === "incarnated") {
            if (materializeOpacity < 1) setMaterializeOpacity(1)
        } else if (phase === "materializing") {
            const started = descentStartedAt ?? performance.now()
            const { descentMs: dMs, materializeMs: mMs } =
                usePlayerStore.getState()
            const elapsed = performance.now() - started
            // Fade-in del avatar durante la ventana materializeMs.
            // Empieza al final de descentMs (cuando el haz toca el pad).
            const into = Math.max(0, elapsed - dMs)
            const t = mMs > 0 ? Math.min(1, into / mMs) : 0
            // Easing: aparece más rápido al final (cubic-out).
            const eased = 1 - Math.pow(1 - t, 3)
            if (Math.abs(materializeOpacity - eased) > 0.005)
                setMaterializeOpacity(eased)
        } else if (materializeOpacity !== 0) {
            setMaterializeOpacity(0)
        }

        // Movimiento bloqueado mientras no está incarnated.
        const blocked =
            linkPhase === "sealed" || phase !== "incarnated"

        // Modificador correr (Space mantenido en escritorio).
        const isRunning = isSpaceDownRef.current

        // 2026-05-05 v6 — Cálculo del arco del salto. Mientras
        // isJumpingRef es true, el cuerpo se eleva siguiendo una
        // parábola sin(πt): entra suave, llega al pico en t=0.5,
        // baja suave. Aplicamos esa altura visual a g.position.y
        // al final del frame; la posición XZ la sigue manejando el
        // target. Si jumpY > RING_OBSTACLE_HEIGHT durante el arco,
        // el avatar puede cruzar el aro obstáculo.
        let jumpY = 0
        if (isJumpingRef.current && jumpStartedAtRef.current != null) {
            const elapsed =
                performance.now() - jumpStartedAtRef.current
            const tj = Math.max(
                0,
                Math.min(1, elapsed / JUMP_DURATION_MS)
            )
            jumpY = JUMP_PEAK * Math.sin(Math.PI * tj)
        }
        const isAirborne = isJumpingRef.current

        // Movimiento por click — velocidad CONSTANTE en lugar de damp3.
        // 2026-05-05 v6 — Sumamos:
        //   · Boundary clamp del Domo (WALK_BOUNDARY_RADIUS).
        //   · Anillo obstáculo: si el step P→Next cruza el aro y el
        //     avatar NO está suficientemente alto en su salto,
        //     proyectamos al borde del aro.
        //   · Forward boost durante el salto: mantiene momentum
        //     hacia el target en air-time.
        // 2026-05-05 v7 — Detectamos "stuck" después del clamp final:
        //   si el delta XZ es menor que STUCK_DELTA_EPSILON pero
        //   teníamos target activo, el aro o el boundary nos topó →
        //   cancelamos el target para que la animación pase a idle
        //   (el cuerpo se queda mirando hacia donde quería ir).
        const currentTarget = usePlayerStore.getState().target
        let isMoving = false
        if (currentTarget && !blocked) {
            tempVec.set(currentTarget[0], 0, currentTarget[2])
            const direction = tempVec.clone().sub(g.position)
            direction.y = 0
            const distance = direction.length()
            if (distance > ARRIVAL_EPSILON) {
                isMoving = true
                direction.normalize()
                let speed = isRunning ? RUN_SPEED : WALK_SPEED
                if (isAirborne) speed += JUMP_FORWARD_BOOST
                const step = Math.min(speed * dt, distance)
                const nextX =
                    g.position.x + direction.x * step
                const nextZ =
                    g.position.z + direction.z * step

                // Anillo obstáculo central: clamp al borde si el cuerpo
                // no tiene altura suficiente para saltarlo.
                let resolvedX = nextX
                let resolvedZ = nextZ
                const canPassRing =
                    jumpY > RING_OBSTACLE_HEIGHT
                if (
                    !canPassRing &&
                    pathCrossesRing(
                        g.position.x,
                        g.position.z,
                        nextX,
                        nextZ
                    )
                ) {
                    const edge = projectToRingEdge(
                        g.position.x,
                        g.position.z,
                        nextX,
                        nextZ
                    )
                    resolvedX = edge.x
                    resolvedZ = edge.z
                }
                // 2026-05-06 — Anillo del portal de Reconfiguración:
                // pared sólida igual que el aro central. La única
                // entrada es por el gap (lado -X mirando al centro
                // del Domo). El salto no abre paso porque el aro
                // dorado está más alto que el aro central (RING_HEIGHT
                // 0.4 vs 0.5 del central) — lo dejamos consistente
                // pero la clave es que el avatar choca contra él al
                // intentar atravesarlo de costado o de afuera.
                if (
                    pathCrossesElectionPortal(
                        g.position.x,
                        g.position.z,
                        resolvedX,
                        resolvedZ
                    )
                ) {
                    const portalEdge = projectToElectionPortalEdge(
                        g.position.x,
                        g.position.z,
                        resolvedX,
                        resolvedZ
                    )
                    resolvedX = portalEdge.x
                    resolvedZ = portalEdge.z
                }

                // Boundary del Domo — clamp circular.
                const clamped = clampToBoundary(
                    resolvedX,
                    resolvedZ,
                    WALK_BOUNDARY_RADIUS
                )
                const beforeX = g.position.x
                const beforeZ = g.position.z
                g.position.x = clamped.x
                g.position.z = clamped.z
                // Detectar stuck: si el avance fue insignificante pero
                // todavía queremos llegar al target, pasamos a idle.
                const movedDx = g.position.x - beforeX
                const movedDz = g.position.z - beforeZ
                const movedSq = movedDx * movedDx + movedDz * movedDz
                if (movedSq < STUCK_DELTA_EPSILON * STUCK_DELTA_EPSILON) {
                    isMoving = false
                    // Mira hacia donde quería ir antes de cancelar —
                    // gesto natural de "ahí está mi destino".
                    g.lookAt(tempVec.x, g.position.y, tempVec.z)
                    usePlayerStore.getState().setTarget(null)
                } else {
                    // Mira hacia el destino.
                    g.lookAt(tempVec.x, g.position.y, tempVec.z)
                }
            }
            if (g.position.distanceTo(tempVec) < ARRIVAL_EPSILON) {
                usePlayerStore.getState().setTarget(null)
            }
        }

        // 2026-05-05 v7 — Sincronización yaw avatar ↔ cameraYaw en
        // primera persona.
        //   · Si el avatar caminó → extraemos yaw del lookAt y lo
        //     escribimos a cameraYaw, así la cámara hereda la
        //     dirección del cuerpo (mira hacia donde se camina).
        //   · Si el avatar está idle → leemos cameraYaw y slerpamos
        //     el quaternion del cuerpo hacia esa orientación, así el
        //     avatar gira para encarar la dirección de la mirada.
        if (cameraMode === "firstPerson" && phase === "incarnated") {
            if (isMoving) {
                fpYawEuler.setFromQuaternion(g.quaternion, "YXZ")
                const yaw = fpYawEuler.y
                const stored = usePlayerStore.getState().cameraYaw
                if (Math.abs(yaw - stored) > 0.001) {
                    usePlayerStore.getState().setCameraYaw(yaw)
                }
            } else {
                const desiredYaw = usePlayerStore.getState().cameraYaw
                fpYawEuler.set(0, desiredYaw, 0, "YXZ")
                fpYawQuaternion.setFromEuler(fpYawEuler)
                dampQ(g.quaternion, fpYawQuaternion, FP_IDLE_YAW_SLERP, dt)
            }
        }

        // Aplicamos la altura del salto al final (después del clamp
        // XZ). Si no hay salto, jumpY = 0 y .y queda en piso.
        g.position.y = jumpY

        // animationKey por INTENCIÓN, no por velocidad medida.
        // Antes el EMA de velocidad tardaba ~3-5 frames en converger a
        // partir de cero → cuando el Tripulante hacía Space+click, los
        // primeros frames se renderizaban en "walk" (porque speedRef
        // todavía no superaba el threshold de "run"). Ahora animKey se
        // deriva directo del estado: si hay target activo y no estoy
        // bloqueado, ya estoy en walk/run desde el primer frame.
        // Prioridad: jump > wave > run/walk > idle. El salto pisa
        // todo porque es la acción más fugaz; cuando el timer
        // termina, volvemos a evaluar el resto.
        let desired: AvatarAnimKey
        if (isJumpingRef.current && !blocked) {
            desired = "jump"
        } else if (isWavingRef.current && !blocked) {
            desired = "wave"
        } else if (isMoving) {
            desired = isRunning ? "run" : "walk"
        } else {
            desired = "idle"
        }
        if (desired !== lastAnimDecisionRef.current) {
            lastAnimDecisionRef.current = desired
            setAnimKey(desired)
        }

        // Sincronizar posición y rotación al store global. La
        // rotación la lee CameraRig en modo firstPerson para apuntar
        // la cámara hacia donde mira el avatar (el quaternion
        // proviene del lookAt al target durante el caminado).
        usePlayerStore
            .getState()
            .setPosition([g.position.x, g.position.y, g.position.z])
        const qq = g.quaternion
        usePlayerStore
            .getState()
            .setRotation([qq.x, qq.y, qq.z, qq.w])

        // Emisión: solo si está incarnated, no bloqueado, throttle 10Hz, y
        // solo cuando realmente nos movimos desde el último envío.
        if (blocked) return
        const now = performance.now()
        if (now - lastSent.current < SEND_INTERVAL_MS) return

        const dx = g.position.x - lastSentPos.current[0]
        const dz = g.position.z - lastSentPos.current[2]
        if (dx * dx + dz * dz < POSITION_DELTA_EPSILON * POSITION_DELTA_EPSILON)
            return

        lastSent.current = now
        lastSentPos.current = [g.position.x, g.position.y, g.position.z]

        const socket = getSocket()
        if (!socket?.connected) return

        const q = g.quaternion
        socket.emit(SOCKET_EVENTS.PLAYER_MOVE, {
            position: [g.position.x, g.position.y, g.position.z],
            rotation: [q.x, q.y, q.z, q.w],
            ts: Date.now(),
        })
    })

    if (phase === "void") return null

    // 2026-05-06 — Durante ascending mostramos solo el orbe luminoso
    // ascendiendo al cielo (transmutación). El cuerpo se desmonta;
    // los handlers de tap, indicador de target y emisión de movimiento
    // tampoco aplican porque ya no hay cuerpo. Sigue siendo el avatar
    // local en el sentido de que su `pad` viene de la posición actual.
    const ascentStartedAt = usePlayerStore.getState().ascentStartedAt
    const ascentDurationMs = usePlayerStore.getState().ascentDurationMs
    if (
        phase === "ascending" &&
        model &&
        polarity &&
        ascentStartedAt != null
    ) {
        return (
            <AscentOrb
                pad={usePlayerStore.getState().position}
                model={model}
                polarity={polarity}
                startedAt={ascentStartedAt}
                durationMs={ascentDurationMs}
            />
        )
    }

    const showBeam =
        (phase === "descending" || phase === "materializing") &&
        model &&
        polarity &&
        descentStartedAt != null

    return (
        <>
            {showBeam && (
                <DescentBeam
                    pad={spawnPad}
                    model={model!}
                    polarity={polarity!}
                    startedAt={descentStartedAt!}
                    descentMs={usePlayerStore.getState().descentMs}
                    materializeMs={
                        usePlayerStore.getState().materializeMs
                    }
                />
            )}
            <group ref={groupRef}>
                {/* 2026-05-06 — Hitbox de tap removido: el propio avatar
                    ya no abre el panel de inspección al picarlo. El
                    raycaster de pointerdown atraviesa al avatar local
                    y va al MovementPlane, lo que permite caminar a
                    través de tu propia silueta para llegar a un punto
                    detrás (igual mecánica que peers anclados). */}
                {/* 2026-05-05 v6 — En primera persona ocultamos el
                    cuerpo del avatar local: la cámara está EN su
                    cabeza, no queremos ver la nuca, los hombros ni
                    el nametag flotando enfrente de la cámara. Los
                    handlers de tap, el TargetIndicator y la emisión
                    de posición siguen activos — solo el visual del
                    cuerpo se silencia. */}
                {cameraMode !== "firstPerson" && (
                    <Avatar
                        siliconIndex={siliconIndex}
                        displayName={displayName}
                        isLocal
                        isAnchored={isAnchored}
                        model={model}
                        polarity={polarity}
                        materializeOpacity={materializeOpacity}
                        animationKey={animKey}
                    />
                )}
            </group>
            {target && linkPhase !== "sealed" && phase === "incarnated" && (
                <TargetIndicator target={target} color="#D4AF37" />
            )}
        </>
    )
}
